// Water surface planes, one instanced hex plane per water biome with a vertex wave (the shared `waterTime`
// clock). Sea tiles (ocean / coast) take their colour, shallows and foam from the world-space shore field
// (waterShader.ts + shore/), so the sea is one continuous surface rather than a mosaic of tile-coloured hexes;
// river tiles keep their per-edge channel drawing.
import type { SceneNode } from '@aod/protocol';
import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useSettings } from '../settings/useSettings.js';
import { dayNightState } from './DayNightLighting.js';
import { RenderLayer } from './RenderLayer.js';
import { waterSurfaceY } from './elevation.js';
import { shoreUniforms } from './shore/shoreBoard.js';
import { buildTileEdgeAttributes, setInstancedAttribute } from './terrainEdges.js';
import { riverWaterShader, seaWaterShader, waterLight } from './waterShader.js';
import { WATER_SKIRT_DEPTH } from './waterSkirt.js';

const HEX_SIZE = 1;
/** Shared wave clock — the debug diorama also drives/reads this. */
export const waterTime = { value: 0 };

// Module-level singletons mutated in place during instance updates.
const _dummy = new THREE.Object3D();
const _color = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };

export function makeHexPlane(): THREE.BufferGeometry {
  // A raw ShapeGeometry hexagon is only 4 triangles, so the vertex wave
  // becomes a piecewise-linear sheet — the shoreline intersection with the
  // land walls visibly kinked in triangle steps. Subdivide with two inner
  // rings (36 triangles) so the bobbing surface reads smooth.
  const verts: number[] = [0, 0, 0];
  const idx: number[] = [];
  const RINGS = [1 / 3, 2 / 3, 1];
  let prevRing: number[] | null = null;
  for (const r of RINGS) {
    const ring: number[] = [];
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 180) * (30 + 60 * i);
      verts.push(HEX_SIZE * r * Math.cos(angle), HEX_SIZE * r * Math.sin(angle), 0);
      ring.push(verts.length / 3 - 1);
    }
    if (prevRing === null) {
      for (let i = 0; i < 6; i++) idx.push(0, ring[i]!, ring[(i + 1) % 6]!);
    } else {
      for (let i = 0; i < 6; i++) {
        const a = prevRing[i]!;
        const b = prevRing[(i + 1) % 6]!;
        const c = ring[i]!;
        const d = ring[(i + 1) % 6]!;
        // CCW quad a→c→d→b splits as (a,c,d) + (a,d,b); the naive
        // (a,b,d)/(a,d,c) flips the winding and backface-culls the rim.
        idx.push(a, c, d, a, d, b);
      }
    }
    prevRing = ring;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(verts, 3));
  g.setIndex(idx);
  g.rotateX(-Math.PI / 2);
  addSkirt(g, prevRing ?? []);
  return g;
}

/**
 * Vertical skirt around the rim, same material as the surface. Water planes sit at different heights by depth
 * class (coast -0.08, shallows -0.23, deep -0.53), and with nothing under a higher plane's edge you could see
 * through the step to whatever lies beneath (the dark polygons between hexes of the sea). The skirt closes the
 * step; it takes its colour from the rim's world position, so it reads as the surface carrying on, not a wall.
 */
function addSkirt(g: THREE.BufferGeometry, rim: number[]): void {
  const pos = g.getAttribute('position') as THREE.BufferAttribute;
  const base = pos.count;
  const out = Array.from(pos.array as Float32Array);
  const idx = Array.from(g.index!.array as Uint16Array | Uint32Array);
  for (const i of rim) out.push(pos.getX(i), -WATER_SKIRT_DEPTH, pos.getZ(i));
  for (let k = 0; k < rim.length; k++) {
    const a = rim[k]!;
    const b = rim[(k + 1) % rim.length]!;
    const c = base + k;
    const d = base + ((k + 1) % rim.length);
    // Face outward (away from the hex centre): wind (a, c, d) / (a, d, b) by the sign of the outward normal.
    const mx = (pos.getX(a) + pos.getX(b)) / 2;
    const mz = (pos.getZ(a) + pos.getZ(b)) / 2;
    const ex = pos.getX(b) - pos.getX(a);
    const ez = pos.getZ(b) - pos.getZ(a);
    // Normal of triangle (a, c, d): (c - a) x (d - a) = (0,-h,0) x (ex,-h,ez) = (-h ez, 0, h ex).
    const nx = -WATER_SKIRT_DEPTH * ez;
    const nz = WATER_SKIRT_DEPTH * ex;
    const outward = nx * mx + nz * mz >= 0;
    if (outward) idx.push(a, c, d, a, d, b);
    else idx.push(a, d, c, a, b, d);
  }
  g.setAttribute('position', new THREE.Float32BufferAttribute(out, 3));
  g.setIndex(idx);
}

/** Only rivers are coloured per tile now (sea colour comes from the shore field). */
const RIVER_COLOR = '#3b82a4';

const WAVE_AMP = { deep: 0.05, shallow: 0.04, coast: 0.02, river: 0.015 } as const;
export type WaterDepthClass = keyof typeof WAVE_AMP;

/**
 * Opaque, unlit water with a vertex wave and a crest shimmer. Sea classes are shaded entirely from the shared
 * shore-field uniforms (`shoreUniforms`); `river` reads its colour from the instance colour.
 */
export function createWaterMaterial(depthClass: WaterDepthClass): THREE.MeshBasicMaterial {
  const amp = WAVE_AMP[depthClass];
  const isRiver = depthClass === 'river';
  const edge = isRiver ? riverWaterShader() : seaWaterShader();
  const mat = new THREE.MeshBasicMaterial({ color: 'rgb(255,255,255)', fog: true });

  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uTime = waterTime;
    if (!isRiver) {
      Object.assign(shader.uniforms, shoreUniforms);
      // Sun + amplitude for the analytic normal, fresnel and glint.
      shader.uniforms.uSunDir = waterLight.dir;
      shader.uniforms.uSunColor = waterLight.color;
      shader.uniforms.uSunInt = waterLight.intensity;
      shader.uniforms.uWaveAmp = { value: amp };
    }
    shader.vertexShader =
      `uniform float uTime;\nvarying float vWave;\nvarying vec2 vWorldXZ;\n${edge.vertexPars}\n${shader.vertexShader}`.replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
      ${edge.vertexMain}
      vec4 _ww = instanceMatrix * vec4(transformed, 1.0);
      float _w = sin(_ww.x * 2.5 + uTime) * cos(_ww.z * 2.5 + uTime * 0.7);
      _w += 0.6 * sin(_ww.x * 5.0 + uTime * 1.3) * sin(_ww.z * 5.0 - uTime * 0.9);
      _w += 0.35 * sin((_ww.x + _ww.z) * 1.1 + uTime * 0.5);
      vWave = _w;
      vWorldXZ = _ww.xz;
      transformed.y += _w * ${amp.toFixed(4)};`,
      );
    shader.fragmentShader =
      `uniform float uTime;\nvarying float vWave;\nvarying vec2 vWorldXZ;\n${edge.fragmentPars}\n${shader.fragmentShader}`.replace(
        '#include <color_fragment>',
        `#include <color_fragment>
      float _bankMask = 0.0;
      ${edge.fragmentMain}
      // Crests catch the light; troughs deepen. Fine ripples add a soft shimmer
      // (on the water only: a river tile's bank stays still).
      float _crest = smoothstep(0.6, 1.6, vWave);
      float _ripple = sin(vWorldXZ.x * 9.0 + uTime * 1.7) * sin(vWorldXZ.y * 8.0 - uTime * 1.3);
      vec3 _shimmer = diffuseColor.rgb * (0.94 + 0.06 * clamp(vWave, -1.0, 1.0));
      _shimmer += vec3(0.05, 0.065, 0.07) * _crest + vec3(0.012) * max(_ripple, 0.0);
      diffuseColor.rgb = mix(_shimmer, diffuseColor.rgb, _bankMask);`,
      );
  };
  // Same onBeforeCompile source for every depth class: key the programs apart.
  mat.customProgramCacheKey = () => `terrain-water:${isRiver ? 'river' : 'sea'}:${depthClass}`;
  return mat;
}

interface TerrainWaterProps {
  readonly entries: Array<{ tile: SceneNode; idx: number }>;
  readonly biome: string;
  readonly count: number;
  readonly biomeMap: Map<string, string>;
  readonly visibleCoords?: Set<string> | null;
  readonly exploredCoords?: Set<string>;
  /** "q,r" -> packed base colour of every tile (buildTileBaseColors): river banks. */
  readonly baseColors?: ReadonlyMap<string, number>;
}

export function TerrainWater({
  entries,
  biome,
  count,
  biomeMap,
  visibleCoords,
  exploredCoords,
  baseColors,
}: TerrainWaterProps) {
  const meshRef = useRef<THREE.InstancedMesh>(null);
  const waterGeo = useMemo(() => makeHexPlane(), []);

  const depthClass: WaterDepthClass =
    biome === 'river' ? 'river' : biome === 'coast' ? 'coast' : 'shallow';

  const waterMat = useMemo(() => createWaterMaterial(depthClass), [depthClass]);

  // Position instances; fog-of-war tone and river-bank data ride in instanced attributes.
  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;

    const dummy = _dummy;
    const color = _color;
    const isRiver = biome === 'river';
    entries.forEach(({ tile }, localIdx) => {
      const [x, , z] = tile.transform.position;
      const props = tile.props as Record<string, unknown> | undefined;
      const q = (props?.q as number) ?? 0;
      const r = (props?.r as number) ?? 0;

      // Shared with the hex outlines and anything floating on the water.
      const waterY = waterSurfaceY(q, r, biomeMap);

      dummy.position.set(x, waterY, z);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      mesh.setMatrixAt(localIdx, dummy.matrix);

      // Sea tiles are coloured by the shore field (white here, fog is the aTone attribute); a river keeps its
      // own colour, dimmed here by fog of war (three states: visible, explored-not-visible, unexplored).
      color.set(isRiver ? RIVER_COLOR : '#ffffff');
      const key = `${q},${r}`;
      if (isRiver && visibleCoords && !visibleCoords.has(key)) {
        color.getHSL(_hsl);
        if (exploredCoords?.has(key)) color.setHSL(_hsl.h, _hsl.s * 0.4, _hsl.l * 0.55);
        else color.setHSL(_hsl.h, _hsl.s * 0.15, _hsl.l * 0.25);
      }
      mesh.setColorAt(localIdx, color);
    });

    // Fog-of-war tone for every class; river tiles also get their bank / channel data.
    const attrs = buildTileEdgeAttributes(
      entries.map(({ tile }) => {
        const props = tile.props as Record<string, unknown> | undefined;
        return [(props?.q as number) ?? 0, (props?.r as number) ?? 0] as const;
      }),
      biomeMap,
      baseColors ?? new Map(),
      visibleCoords,
      exploredCoords,
    );
    setInstancedAttribute(waterGeo, 'aTone', attrs.tone, 3);
    if (isRiver) {
      setInstancedAttribute(waterGeo, 'aEdgeMask', attrs.masks, 4);
      setInstancedAttribute(waterGeo, 'aNbA', attrs.nbA, 3);
      setInstancedAttribute(waterGeo, 'aNbB', attrs.nbB, 3);
    }

    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.count = count;
    // Refresh the cached bounding sphere — see TerrainTiles for why.
    mesh.computeBoundingSphere();
  }, [entries, count, biome, biomeMap, visibleCoords, exploredCoords, baseColors, waterGeo]);

  return (
    <>
      <instancedMesh
        ref={meshRef}
        args={[waterGeo, waterMat, count]}
        renderOrder={RenderLayer.Terrain + 1}
        frustumCulled
      />
    </>
  );
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

/**
 * Drives the shared water shader time uniform. Must be rendered in the scene. Frozen (a still sea) under
 * prefers-reduced-motion or with the particle-effects setting off, like the other ambient effects.
 */
export function WaterAnimator() {
  const particleEffects = useSettings((s) => s.particleEffects);
  const reduced = useMemo(prefersReducedMotion, []);
  useFrame((_, delta) => {
    // The sun feeds the water's fresnel and glint, so it updates even when the
    // waves are frozen.
    const el = Math.max(-1, Math.min(1, dayNightState.sunHeight));
    const azimuth = Math.asin(el);
    waterLight.dir.value
      .set(0.55 * Math.cos(azimuth), Math.max(0.14, el), 0.3 * Math.sin(azimuth) + 0.25)
      .normalize();
    // Same intensity/warmth curve as DayNightLighting's directional light.
    waterLight.intensity.value = 0.35 + 0.85 * Math.max(0, el);
    const warmth = 1 - Math.abs(el);
    waterLight.color.value.setRGB(1, 0.88 + warmth * 0.12, 0.7 + warmth * 0.3);
    if (reduced || !particleEffects) return;
    waterTime.value += delta;
  });
  return null;
}
